import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    OnInit
} from "@angular/core";
import {Router} from "@angular/router";
import {MdxMemberUtil} from "../../../framework/bi/mdx-member-util";
import {FilterModel, provideFilterModel} from "../../../framework/bi/filter/filter.model";
import {DashboardLayout, DashboardRow} from "../dashboard-layout";
import {DashboardChartData} from "../dashboard.data";
import {DashboardEntityModel} from "../dashboard-entity.model";
import {ConfigurationService, EntityData, EntityModelStatus, provideEntityModel} from "@fedeltapointofsale/data-client";
import {CubeDefinitionData} from "../../../framework/bi/cube-definition.data";
import {BIService} from "../../../framework/bi/bi.service";
import {AbstractEditPage} from "../../../framework/entity/edit/edit-page/abstract-edit-page";
import {BIAttribute} from "../../draggable-attributes-list/bi-attribute";
import {EditPageActionEvent} from "../../../framework/entity/edit/edit-page/edit-page-action.event";
import {EditPageComponent} from "../../../framework/entity/edit/edit-page/edit-page.component";
import {DashboardModel} from "../dashboard.model";
import {FUIAlertActions, FUIAlertService, FUIBreakpoints, FUIResponsiveService} from "@fedeltapointofsale/ui";
import {Config, ServerAuth} from "../../../config";
import {ServerStatus, ServerStatusData} from "../../../framework/bi/server-status/server-status.data";
import {ServerStatusModel} from "../../../framework/bi/server-status/server-status.model";
import {first, Subject, Subscription} from "rxjs";
import {TranslateService} from "@ngx-translate/core";

@Component({
    selector: "wbo-bi-dashboard-edit-page",
    templateUrl: "./dashboard-edit-page.component.html",
    styleUrls: ["./dashboard-edit-page.component.scss"],
    providers: [provideFilterModel(), provideEntityModel(DashboardEntityModel), BIService, DashboardModel, ServerStatusModel],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardEditPageComponent extends AbstractEditPage implements OnInit, OnDestroy {
    public layout: DashboardLayout;
    public cubeDefinition: CubeDefinitionData = new CubeDefinitionData();
    public attributes: BIAttribute[];
    public mandatoryAttribute: BIAttribute;
    public mandatoryAttributeFieldMessage: string;
    public mandatoryAttributeFilterMessage: string;
    public selectedAttribute: BIAttribute;
    public selectedAttributeLabel: string;
    public selectedChart: DashboardChartData;
    public selectedMeasures: BIAttribute[] = [];
    public selectedFilters: BIAttribute[] = [];
    public selectedTopRef: string;
    public selectedRefLine: string;
    public selectedTitle: string;
    public dashboardChatUpdated: Subject<DashboardChartData> = new Subject<DashboardChartData>();
    exitRoute: string;
    public fullEdit: boolean;
    public canPublish: boolean = false;
    public cubeName: string;
    private subscriptions: Subscription = new Subscription();

    constructor(public filterModel: FilterModel,
                public model: DashboardEntityModel,
                private biService: BIService,
                private router: Router,
                private translateService: TranslateService,
                responsiveService: FUIResponsiveService,
                changeDetectorRef: ChangeDetectorRef,
                private configService: ConfigurationService,
                private alertService: FUIAlertService,
                private element: ElementRef,
                private ngZone: NgZone,
                public serverStatusModel: ServerStatusModel,
                public dashboardModel: DashboardModel) {
        super(changeDetectorRef, responsiveService);

        // enable dragover on this page
        this.ngZone.runOutsideAngular(() => {
            this.element.nativeElement.addEventListener("dragover", (dragEvent: DragEvent) => {
                dragEvent.stopPropagation();
            });
        });

        this.fullEdit = this.responsiveService.breakpointApplies(FUIBreakpoints.mediumUp);
        this.subscriptions.add(this.responsiveService.windowWidth$.subscribe(() => {
                this.fullEdit = this.responsiveService.breakpointApplies(FUIBreakpoints.mediumUp);
                this.changeDetectorRef.markForCheck();
            }
        ));

        this.configService.getBoolean(ServerAuth.PUBLISH_BI_DASHBOARD).then((canPublish: boolean) => {
            this.canPublish = canPublish;
            this.changeDetectorRef.markForCheck();
        });
    }

    get dashboardDateRange(): string {
        return this.model.data.dateRangeRef;
    }

    set dashboardDateRange(dateRange: string) {
        if (dateRange !== this.model.data.dateRangeRef) {
            this.model.data.dateRangeRef = dateRange;
            this.filterModel.setFilterValue(FilterModel.DATE_RANGE_FILTER_NAME, dateRange);
        }
    }

    ngOnInit(): void {
        this.configService.getBoolean(Config.BI_ENABLED).then(biEnabled => {
            if (!biEnabled) {
                this.router.navigate(["/main/home"], {replaceUrl: true});
            }
        });

        this.model.$data.pipe(first()).subscribe(() => {
            if (this.model.status === EntityModelStatus.INSERT) {
                this.exitRoute = EditPageComponent.EXIT_ROUTE_BACK;
                this.model.data.cubeName = this.biService.getDefaultCube();
                this.cubeName = this.model.data.cubeName;
                this.init(this.model.data.cubeName);
            } else {
                this.cubeName = this.model.data.cubeName;
                this.init(this.model.data.cubeName);
                this.exitRoute = "dashboard/" + this.model.data.id;
            }
            if (this.model.data.charts.length) {
                this.layout = DashboardLayout.buildLayout(this.model.data, false);
            } else {
                this.initEmptyDashboards();
            }

            this.changeDetectorRef.markForCheck();
        });
    }

    init(cubeName: string): void {
        this.subscriptions.add(this.serverStatusModel.statusUpdate$.subscribe((status: ServerStatusData) => {
            if (status.status === ServerStatus.INITIALISED) {
                this.loadCube(cubeName);
            } else {
                console.log("BI Server status: " + status.status);
                console.log(status.percentComplete + "% complete");
            }
            this.changeDetectorRef.markForCheck();
        }));
    }

    loadCube(cubeName: string): void {
        this.biService.getCube(cubeName).then((cubeDefinition: CubeDefinitionData) => {
            this.cubeDefinition = cubeDefinition;

            this.filterModel.init(this.cubeDefinition, this.model.data.dateRangeRef, this.model.data.filterMembers);

            this.attributes = BIAttribute.buildAttributesFromCube(cubeDefinition).sort((a, b) => this.translateService.instant(a.label).localeCompare(this.translateService.instant(b.label)));

            this.mandatoryAttribute = this.attributes.find(att => att.mandatory);
            if (this.mandatoryAttribute) {
                this.mandatoryAttributeFieldMessage = this.translateService.instant("wbo.bi-dashboard.edit.field-set.mandatory.field", {"attribute": this.translateService.instant(this.mandatoryAttribute.label)});
                this.mandatoryAttributeFilterMessage = this.translateService.instant("wbo.bi-dashboard.edit.field-set.mandatory.filter", {"attribute": this.translateService.instant(this.mandatoryAttribute.label)});
            } else {
                this.mandatoryAttributeFieldMessage = undefined;
                this.mandatoryAttributeFilterMessage = undefined;
            }

            if (this.model.data.filterMembers && this.model.data.filterMembers.length > 0) {
                this.selectedFilters = [];
                this.model.data.filterMembers.forEach((m: string) => {
                    const selected = this.attributes.find((i: BIAttribute) => i.value === MdxMemberUtil.getHierarchy(m));
                    if (selected && this.selectedFilters.indexOf(selected) === -1) {
                        this.selectedFilters.push(selected);
                    }
                });
            }

            if (this.layout && this.layout.rows.length !== 0 && this.layout.rows[0].columns && this.layout.rows[0].columns.length !== 0) {
                this.selectChart(this.layout.rows[0].columns[0]);
            }

            this.changeDetectorRef.markForCheck();
        });
    }

    initEmptyDashboards(): void {
        this.layout = new DashboardLayout();
        this.layout.rows = [];
        this.model.data.charts = [];
        this.model.data.filterMembers = [];
        let row: DashboardRow = <DashboardRow>EntityData.create(DashboardRow);
        this.layout.rows.push(row);
        this.selectedChart = row.addNewChart();
        row.addNewChart();

        row = <DashboardRow>EntityData.create(DashboardRow);
        this.layout.rows.push(row);
        row.addNewChart();
        row.addNewChart();

        row = <DashboardRow>EntityData.create(DashboardRow);
        this.layout.rows.push(row);
        row.addNewChart();
        row.addNewChart();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    onChangeCubeName(): void {
        if (this.layout.hasChartData()) {
            this.alertService.showConfirmationOkCancel("wbo.bi-dashboard.prompt.changeCube.heading", "wbo.bi-dashboard.prompt.changeCube.message").subscribe((action) => {
                if (action === FUIAlertActions.OK_ACTION) {
                    this.model.data.cubeName = this.cubeName;
                    this.initEmptyDashboards();
                    this.init(this.model.data.cubeName);
                } else {
                    this.cubeName = this.model.data.cubeName;
                    this.changeDetectorRef.markForCheck();
                }
            });
        } else {
            this.model.data.cubeName = this.cubeName;
            this.init(this.model.data.cubeName);
        }
    }

    public selectAttribute(item: BIAttribute): void {
        this.selectedAttribute = item;
        this.selectedChart.attribute = item.value;
        this.selectedChart.selectedAttribute = this.selectedChart.attribute;
        this.selectedChart.filterMembers = [];
        this.translateService.get(item.label).subscribe(value => {
            this.selectedChart.title = value;
            this.selectedTitle = value;
        });
        this.updateChart();
    }

    public removeAttribute(): void {
        this.selectedAttribute = undefined;
        this.selectedChart.attribute = undefined;
        this.selectedChart.selectedAttribute = undefined;
        this.selectedChart.filterMembers = [];

        this.updateChartAfterRemoval();
    }

    public addMeasureAt(item: BIAttribute, i: number): void {
        if (!this.selectedMeasures || this.selectedMeasures.length === 0) {
            this.selectedChart.measures = [];
        }
        if (!this.selectedChart.measures) {
            this.selectedChart.measures = [];
        }

        const currentIdx: number = this.selectedMeasures.indexOf(item);
        if (currentIdx === i) {
            return;
        }

        if (currentIdx >= 0) {
            this.selectedMeasures.splice(currentIdx, 1);
            this.selectedChart.measures.splice(currentIdx, 1);
            if (i > currentIdx) {
                i--;
            }
        }
        this.selectedMeasures.splice(i, 0, item);
        this.selectedChart.measures.splice(i, 0, item.value);
        this.selectedChart.selectedMeasures = this.selectedChart.measures;
        this.updateChart();
    }

    public appendMeasure(item: BIAttribute): void {
        this.addMeasureAt(item, this.selectedMeasures.length);
    }

    public removeMeasure(i: number): void {
        if (i !== -1) {
            this.selectedMeasures.splice(i, 1);
            this.selectedChart.selectedMeasures.splice(i, 1);
            this.selectedChart.measures = this.selectedChart.selectedMeasures;
            this.updateChartAfterRemoval();
        }
    }

    /**
     * Updates the currently selected chart after a measure or attribute has been removed.
     */
    private updateChartAfterRemoval(): void {
        if (!this.selectedChart.selectedAttribute && (!this.selectedChart.selectedMeasures || this.selectedChart.selectedMeasures.length == 0)) {
            this.selectedChart.title = "New";
            this.selectedChart.chartType = "blank";
            this.selectedTitle = "New";
        }

        this.updateChart();
        this.refreshCurrentChart();

    }

    public selectAttributeOrMeasureOnChart(item: BIAttribute, dashboardChartData: DashboardChartData): void {
        // use setTimeout as when dropping the attribute in the chart and it is blank,
        // ng2-dnd library in the used version has a bug that does a setTimeout and detectChanges after
        // it drops the item causing it to run after the view has been destroyed causing a view destroyed error
        setTimeout(() => {
            this.selectChart(dashboardChartData);
            if (item.type === BIAttribute.DIMENSION_TYPE) {
                this.selectAttribute(item);
            } else {
                this.appendMeasure(item);
            }
        }, 300);
    }

    public selectFilterMembersOnChart(filterMembers: string[]): void {
        this.selectedChart.filterMembers = filterMembers;
        this.refreshCurrentChart();
    }

    public selectDashboardFilterMembersOnChart(item: BIAttribute, filterMembers: string[]): void {
        this.filterModel.setDimensionFilterMembers(item.id, filterMembers);
    }

    public removeDashboardFilterMembersOnChart(item: BIAttribute): void {
        this.filterModel.setDimensionFilterMembers(item.id, []);
    }

    public addFilterAt(item: BIAttribute, i: number): void {
        if (this.selectedFilters.indexOf(item) !== -1) {
            return;
        }

        this.selectedFilters.push(item);
    }

    public removeFilter(i: number): void {
        this.selectedFilters.splice(i, 1);
    }

    public selectChart(dashboardChartData: DashboardChartData) {
        this.selectedChart = dashboardChartData;
        this.selectedTitle = this.selectedChart.title;
        this.selectedTopRef = this.selectedChart.topBottomRef;
        this.selectedRefLine = this.selectedChart.selectedRefLine;
        this.selectedAttribute = undefined;
        this.selectedMeasures = [];
        if (this.selectedChart.selectedAttribute) {
            const selectedAttribute = this.attributes.find((attribute: BIAttribute) => attribute.value === this.selectedChart.selectedAttribute);
            if (selectedAttribute) {
                this.selectedAttribute = selectedAttribute;
                this.selectedAttributeLabel = selectedAttribute.label;
            }
        }

        if (this.selectedChart.selectedMeasures && this.selectedChart.selectedMeasures.length > 0) {
            this.selectedMeasures = [];
            this.selectedChart.selectedMeasures.forEach((measure: string) => {
                const selectedMeasure = this.attributes.find((attribute: BIAttribute) => attribute.value === measure);
                if (selectedMeasure) {
                    this.selectedMeasures.push(selectedMeasure);
                }
            });
        }

        this.changeDetectorRef.markForCheck();
    }

    public titleChange(v: string) {
        if (v !== this.selectedChart.title) {
            this.selectedTitle = v;
            this.selectedChart.title = v;
            this.refreshCurrentChart();
        }
    }

    public topBottomChange(v: string) {
        if (v !== this.selectedChart.topBottomRef) {
            this.selectedTopRef = v;
            this.selectedChart.topBottomRef = v;
            this.refreshCurrentChart();
        }
    }

    public refLineChange(v: string) {
        if (v !== this.selectedChart.selectedRefLine) {
            this.selectedRefLine = v;
            this.selectedChart.selectedRefLine = v;
            this.refreshCurrentChart();
        }
    }

    public isSelected(dashboardChartData: DashboardChartData) {
        return this.selectedChart && this.selectedChart === dashboardChartData;
    }

    public updateChartType(type: string): void {
        const prevType = this.selectedChart.chartType;
        this.selectedChart.chartType = type;

        if (type === "summary" || prevType === "summary") {
            const r = this.findSelectedChartRow();
            if (r) {
                r.columns.forEach((columnChart) => {
                    if (columnChart !== this.selectedChart) {
                        this.loadChart(columnChart);
                    }
                });
            }
        }
        this.updateChart();
    }

    public getChartHeight(row: DashboardRow): number {
        let height = 400;
        row.columns.forEach((dashBoardChartData) => {
            if (dashBoardChartData.chartType === "summary") {
                height = 200;
            }
        });

        return height;
    }

    isMandatoryAttributeSelected(): boolean {
        if (!this.mandatoryAttribute) {
            return true;
        }

        const mandatoryAttributeFilters = this.filterModel.getFilterMembersByDimension(this.mandatoryAttribute.id);
        if (mandatoryAttributeFilters && mandatoryAttributeFilters.length == 1) {
            return true;
        }

        return this.selectedAttribute === this.mandatoryAttribute;
    }

    removeChart(r: DashboardRow, dashboardChartData: DashboardChartData) {
        const cols = [];
        let newBlankChart: DashboardChartData;
        r.columns.forEach(columnChart => {
            if (columnChart === dashboardChartData) {
                if (dashboardChartData.chartType === "blank") {
                    if (r.columns.length === 1) {
                        cols.push(columnChart);
                    }
                } else {
                    // Replace the chart with a blank chart
                    const newChart: DashboardChartData = r.newChart();
                    cols.push(newChart);
                    newBlankChart = newChart;
                }
            } else {
                cols.push(columnChart);
                this.loadChart(columnChart);
            }
        });
        r.columns = cols;

        if (newBlankChart) {
            this.selectChart(newBlankChart);
        } else {
            this.selectChart(r.columns[0]);
        }
    }

    addChart(r: DashboardRow, dashboardChartData: DashboardChartData) {
        const cols = [];
        let newChart: DashboardChartData;
        r.columns.forEach((columnChart) => {
            cols.push(columnChart);

            this.loadChart(columnChart);

            if (columnChart === dashboardChartData) {
                const chart: DashboardChartData = r.newChart();
                cols.push(chart);
                newChart = chart;
            }
        });
        r.columns = cols;
        if (newChart) {
            this.selectChart(newChart);
        }
    }

    refreshCurrentChart() {
        this.loadChart(this.selectedChart);
    }

    onSave(event: EditPageActionEvent): void {
        this.model.data.cubeName = this.cubeDefinition.cubeName;
        this.model.data.filterMembers = this.filterModel.getFilterMembers();
        this.model.data.charts = [];
        for (let rowIndex = 0; rowIndex < this.layout.rows.length; rowIndex++) {
            const row: DashboardRow = this.layout.rows[rowIndex];
            for (let colIndex = 0; colIndex < row.columns.length; colIndex++) {
                const chart: DashboardChartData = row.columns[colIndex];
                chart.layoutRow = rowIndex;
                chart.layoutCol = colIndex;
                this.model.data.charts.push(chart);
            }
        }
        event.actionComplete = Promise.resolve(true);
    }

    trackByFn(index, item) {
        return item.getIdentifier(true);
    }

    saved(): void {
        this.exitRoute = "dashboard/" + this.model.data.id;
        this.editPageComponent.exitRoute = "dashboard/" + this.model.data.id;
    }

    refresh(): void {
        this.filterModel.notifyFilterChange();
    }

    private findSelectedChartRow(): DashboardRow {
        let row: DashboardRow;
        if (this.layout) {
            this.layout.rows.forEach((r: DashboardRow) => {
                r.columns.forEach((columnChart) => {
                    if (columnChart === this.selectedChart) {
                        row = r;
                    }
                });
            });
        }

        return row;
    }

    private loadChart(dashboardChartData: DashboardChartData): void {
        this.dashboardChatUpdated.next(dashboardChartData);
    }

    private updateChart(): void {
        if (this.selectedChart) {
            if ((this.selectedMeasures && this.selectedMeasures.length > 0) || this.selectedAttribute) {
                if (this.selectedChart.chartType === "blank") {
                    this.selectedChart.chartType = "column";
                }

                if ((!this.selectedChart.measures || this.selectedChart.measures.length === 0)) {
                    this.selectedChart.measures = [];
                    this.selectedChart.measures.push(this.cubeDefinition.measures[0]);
                }

                if (!this.selectedChart.attribute) {
                    const mandatory: BIAttribute = this.attributes.find(value => value.mandatory);
                    const dimension: BIAttribute = mandatory != null ? mandatory : this.attributes.find(value => value.type === BIAttribute.DIMENSION_TYPE);
                    this.selectedChart.attribute = dimension.value;
                    this.translateService.get(dimension.label).subscribe(value => {
                        this.selectedChart.title = value;
                        this.selectedTitle = value;
                    });
                }
                this.loadChart(this.selectedChart);
            } else if (this.selectedChart.chartType === "summary") {
                this.updateChartType("blank");
            }

            this.changeDetectorRef.markForCheck();
        }
    }
}
